import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useCallback } from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { Dropdown } from "../../../common_components/FormInputs";
import PagesCommonHeader from "../../../common_components/PagesCommonHeader";
import { selectDesignationWiseUsers } from "../../../redux/clientDataSlice";
import { ALL_NETWORKS } from "../../../utils/networkConstants";
import { AM_DESIGNATIONS, OPERATOR_DESIGNATIONS, collectUsersByDesignations } from "../../../utils/designationFilters";
const ClientHeader = ({ selectedAM, setSelectedAM, selectedOperator, setSelectedOperator, type, setType, network, setNetwork, setPage }) => {
    const designationWiseUsers = useSelector(selectDesignationWiseUsers);
    /* ===== PROCESSED USER LISTS ===== */
    // AM filter → Account Manager designations.
    const accountManagers = useMemo(() => collectUsersByDesignations(designationWiseUsers?.designationMap, AM_DESIGNATIONS), [designationWiseUsers]);
    // Operators filter → Lead designations only.
    const operators = useMemo(() => collectUsersByDesignations(designationWiseUsers?.designationMap, OPERATOR_DESIGNATIONS), [designationWiseUsers]);
    /* ===== DROPDOWN OPTIONS ===== */
    const amOptions = useMemo(() => [
        { value: "", label: "All" },
        ...accountManagers.map(u => ({
            value: u._id,
            label: `${u.first_name} ${u.last_name}`
        }))
    ], [accountManagers]);
    const operatorOptions = useMemo(() => [
        { value: "", label: "All" },
        ...operators.map(u => ({
            value: u._id,
            label: `${u.first_name} ${u.last_name}`
        }))
    ], [operators]);
    const typeOptions = useMemo(() => [
        { value: "paid_media", label: "Paid Media" },
        { value: "affiliate", label: "Affiliate" },
        { value: "aeo", label: "AEO" },
    ], []);
    const networkOptions = useMemo(() => [
        // performance_tracker is a Sync Log-only network, not a connectable client network.
        ...ALL_NETWORKS
            .filter(n => !['performance_tracker', 'channels_revenue'].includes(n.name)).map(n => ({ value: n.name, label: n.label, icon: n.icon }))
    ], []);
    /* ===== DISPLAY VALUES ===== */
    const amDisplayValue = useMemo(() => {
        const selectedLabel = selectedAM
            ? accountManagers.find(u => u._id === selectedAM)
                ? `${accountManagers.find(u => u._id === selectedAM)?.first_name} ${accountManagers.find(u => u._id === selectedAM)?.last_name}`
                : "All"
            : "All";
        return (_jsxs("span", { children: [_jsx("span", { style: { color: "#101828", fontWeight: 500 }, children: "AM:" }), " ", _jsx("span", { style: { color: "#667085" }, children: selectedLabel })] }));
    }, [selectedAM, accountManagers]);
    const operatorDisplayValue = useMemo(() => {
        const selectedLabel = selectedOperator
            ? operators.find(u => u._id === selectedOperator)
                ? `${operators.find(u => u._id === selectedOperator)?.first_name} ${operators.find(u => u._id === selectedOperator)?.last_name}`
                : "All"
            : "All";
        return (_jsxs("span", { children: [_jsx("span", { style: { color: "#101828", fontWeight: 500 }, children: "Operator:" }), " ", _jsx("span", { style: { color: "#667085" }, children: selectedLabel })] }));
    }, [selectedOperator, operators]);
    const typeDisplayValue = useMemo(() => {
        let selectedLabel = "All";
        if (type.length === 1) {
            selectedLabel = typeOptions.find(t => t.value === type[0])?.label || "All";
        }
        else if (type.length > 1) {
            const labels = type.map(t => typeOptions.find(opt => opt.value === t)?.label).filter(Boolean);
            selectedLabel = labels.join(", ");
        }
        return (_jsxs("span", { children: [_jsx("span", { style: { color: "#101828", fontWeight: 500 }, children: "Type:" }), " ", _jsx("span", { style: { color: "#667085" }, children: selectedLabel })] }));
    }, [type, typeOptions]);
    const networkDisplayValue = useMemo(() => {
        let selectedLabel = "All";
        if (network.length === 1) {
            selectedLabel = networkOptions.find(t => t.value === network[0])?.label || "All";
        }
        else if (network.length > 1) {
            const labels = network.map(t => networkOptions.find(opt => opt.value === t)?.label).filter(Boolean);
            selectedLabel = labels.join(", ");
        }
        return (_jsxs("span", { children: [_jsx("span", { style: { color: "#101828", fontWeight: 500 }, children: "Network:" }), " ", _jsx("span", { style: { color: "#667085" }, children: selectedLabel })] }));
    }, [network, networkOptions]);
    /* ===== HANDLERS ===== */
    const handleAMChange = useCallback((val) => {
        setSelectedAM(val);
        setSelectedOperator("");
        setPage(1);
    }, [setSelectedAM, setSelectedOperator, setPage]);
    const handleOperatorChange = useCallback((val) => {
        setSelectedOperator(val);
        setSelectedAM("");
        setPage(1);
    }, [setSelectedAM, setSelectedOperator, setPage]);
    const handleTypeChange = useCallback((val) => {
        setType(val);
        setPage(1);
    }, [setType, setPage]);
    const handleNetworkChange = useCallback((val) => {
        setNetwork(val);
        setPage(1);
    }, [setNetwork, setPage]);
    /* ===== UI ===== */
    return (_jsx(PagesCommonHeader, { height: "58px", title: "Client List", rightSection: _jsxs(Box, { display: "flex", gap: 2, alignItems: "center", children: [_jsx(Dropdown, { value: selectedAM, onChange: handleAMChange, options: amOptions, placeholder: "AM: All", displayValue: amDisplayValue, leftIcon: "/assets/userC.svg", size: "sm", fullWidth: false, className: "header-dropdown", searchable: accountManagers.length > 10 }), _jsx(Dropdown, { value: selectedOperator, onChange: handleOperatorChange, options: operatorOptions, placeholder: "Operator: All", displayValue: operatorDisplayValue, leftIcon: "/assets/userC.svg", size: "sm", fullWidth: false, className: "header-dropdown", searchable: operators.length > 10 }), _jsx(Dropdown, { value: type, onChange: handleTypeChange, options: typeOptions, placeholder: "Type: All", displayValue: typeDisplayValue, leftIcon: "/assets/TypeC.svg", multiple: true, size: "sm", fullWidth: false, className: "header-dropdown" }), _jsx(Dropdown, { value: network, onChange: handleNetworkChange, options: networkOptions, placeholder: "Network: All", displayValue: networkDisplayValue, leftIcon: "/assets/TypeC.svg", multiple: true, size: "sm", fullWidth: false, className: "header-dropdown" })] }) }));
};
export default ClientHeader;
